'use client'

import { useState } from 'react'
import { buttonVariants } from '@cocoonspace/ui/components/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@cocoonspace/ui/components/collapsible'
import { Trans, useLingui } from '@lingui/react/macro'
import {
	ArrowRightIcon,
	CalendarSyncIcon,
	ChevronDownIcon,
	ClockPlusIcon,
	MapPinIcon,
	SendIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'

import { SectionTitle } from '~/domains/home/components/section-title'
import { SectionWrapper } from '~/domains/home/components/section-wrapper'

export const Scheduler = () => {
	const { t } = useLingui()

	const [activeCollapsible, setActiveCollapsible] = useState('calendar-sync')

	return (
		<div className='relative overflow-hidden'>
			<SectionWrapper>
				<SectionTitle>
					<Trans>
						Pour gagner en efficacité, utilisez la prise rendez-vous visio et{' '}
						<span className='text-primary'>présentiel</span>
					</Trans>
				</SectionTitle>

				<div className='flex flex-col items-center gap-6 md:flex-row'>
					<div className='flex flex-col md:w-[500px]'>
						<Collapsible
							className='w-full border-b py-3'
							open={activeCollapsible === 'calendar-sync'}
							onOpenChange={(open) => {
								if (!open) return
								setActiveCollapsible('calendar-sync')
							}}
						>
							<CollapsibleTrigger className='flex w-full items-center gap-2 font-bold'>
								<CalendarSyncIcon className='size-5 shrink-0' />

								<span className='flex-1 text-left'>
									{t`Synchronisez votre calendrier`}
								</span>

								<ChevronDownIcon className='ml-auto size-5 shrink-0' />
							</CollapsibleTrigger>

							<CollapsibleContent>
								{t`Créez votre évènement et synchronisez votre page à votre Google Calendar pour ne manquer aucun rendez-vous.`}
							</CollapsibleContent>
						</Collapsible>

						<Collapsible
							className='w-full border-b py-3'
							open={activeCollapsible === 'add-availability'}
							onOpenChange={(open) => {
								if (!open) return
								setActiveCollapsible('add-availability')
							}}
						>
							<CollapsibleTrigger className='flex w-full items-center gap-2 font-bold'>
								<ClockPlusIcon className='size-5' />
								<span className='flex-1 text-left'>{t`Ajoutez vos disponibilités`}</span>
								<ChevronDownIcon className='ml-auto size-5 shrink-0' />
							</CollapsibleTrigger>

							<CollapsibleContent>
								{t`Tenez vos invités informés de vos disponibilités.`}
							</CollapsibleContent>
						</Collapsible>

						<Collapsible
							className='w-full border-b py-3'
							open={activeCollapsible === 'choose-location'}
							onOpenChange={(open) => {
								if (!open) return
								setActiveCollapsible('choose-location')
							}}
						>
							<CollapsibleTrigger className='flex w-full items-center gap-2 font-bold'>
								<MapPinIcon className='size-5' />
								<span className='flex-1 text-left'>{t`Choisissez l’adresse idéale`}</span>
								<ChevronDownIcon className='ml-auto size-5 shrink-0' />
							</CollapsibleTrigger>

							<CollapsibleContent>
								{t`Sélectionnez une adresse et jusqu’à cinq Cocoons pour recevoir vos clients, en présentiel ou en visio.`}
							</CollapsibleContent>
						</Collapsible>

						<Collapsible
							className='w-full py-3'
							open={activeCollapsible === 'share-link'}
							onOpenChange={(open) => {
								if (!open) return
								setActiveCollapsible('share-link')
							}}
						>
							<CollapsibleTrigger className='flex w-full items-center gap-2 font-bold'>
								<SendIcon className='size-5' />
								<span className='flex-1 text-left'>{t`Partagez votre lien`}</span>
								<ChevronDownIcon className='ml-auto size-5 shrink-0' />
							</CollapsibleTrigger>

							<CollapsibleContent>
								{t`Partagez votre page de prise de rendez-vous et finalisez votre réservation à la réception de la réponse de votre invité.`}
							</CollapsibleContent>
						</Collapsible>

						<Link
							className={buttonVariants({
								size: 'sm',
								variant: 'textPrimary',
								className: 'mt-6 ml-auto',
							})}
							href='https://scheduling.cocoon-space.com'
							rel='noreferrer'
							target='_blank'
							data-umami-event='scheduling-landing-home-click'
						>
							{t`En savoir plus`} <ArrowRightIcon className='ml-1' />
						</Link>
					</div>

					<div className='relative min-h-[333px] w-full flex-1'>
						<AnimatePresence>
							{activeCollapsible === 'calendar-sync' && (
								<motion.div
									key={activeCollapsible}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.3 }}
									className='absolute top-1/2 right-0 -translate-y-1/2'
								>
									<Image
										alt='Calendar sync'
										src='/images/home/scheduler-calendar-sync.png'
										width={500}
										height={333}
									/>
								</motion.div>
							)}

							{activeCollapsible === 'add-availability' && (
								<motion.div
									key={activeCollapsible}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.3 }}
									className='absolute top-1/2 right-0 -translate-y-1/2'
								>
									<Image
										alt='Add availability'
										src='/images/home/scheduler-add-availabilities.png'
										width={500}
										height={333}
									/>
								</motion.div>
							)}

							{activeCollapsible === 'choose-location' && (
								<motion.div
									key={activeCollapsible}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.3 }}
									className='absolute top-1/2 right-0 -translate-y-1/2'
								>
									<Image
										alt='Choose location'
										src='/images/home/scheduler-choose-location.png'
										width={500}
										height={333}
									/>
								</motion.div>
							)}

							{activeCollapsible === 'share-link' && (
								<motion.div
									key={activeCollapsible}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.3 }}
									className='absolute top-1/2 right-0 -translate-y-1/2'
								>
									<Image
										alt='Share link'
										src='/images/home/scheduler-share-link.png'
										width={500}
										height={333}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				</div>
			</SectionWrapper>
		</div>
	)
}
